import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Loader, Paper, Text } from "@mantine/core";
import { Button, Alert } from "@xplatform/design-system";
import { IconArrowLeft, IconPlus } from "@tabler/icons-react";
import { useFlowConfig } from "@hooks/flow";
import { useTranslation } from "@hooks/useTranslation";
import { VersionSelector, FlowDiagram, CreateFlowConfigDialog } from "@src/features";
import type { FlowVersionSummaryViewModel } from "@models/doctype";
import "./DocTypeFlowPage.scss";

export const DocTypeFlowPage = () => {
    const { docTypeId } = useParams<{ docTypeId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    // Passed via navigation state from the document-types list (the human-
    // readable code isn't part of the flow response).
    const docTypeCode = (location.state as { docTypeCode?: string } | null)
        ?.docTypeCode;
    const { t } = useTranslation();
    const {
        flowVersions,
        selectedFlowConfig,
        isLoading,
        error,
        fetchFlowVersions,
        fetchFlowDetail,
        activateFlow,
    } = useFlowConfig();

    const [selectedVersion, setSelectedVersion] =
        useState<FlowVersionSummaryViewModel | null>(null);
    const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
    const [shouldAutoSelectNewest, setShouldAutoSelectNewest] = useState(false);

    useEffect(() => {
        if (docTypeId) {
            fetchFlowVersions(docTypeId);
        }
    }, [docTypeId, fetchFlowVersions]);

    useEffect(() => {
        if (flowVersions.length > 0) {
            if (!selectedVersion || shouldAutoSelectNewest) {
                const newestVersion = flowVersions[0];
                setSelectedVersion(newestVersion);
                setShouldAutoSelectNewest(false);

                if (docTypeId) {
                    fetchFlowDetail(
                        docTypeId,
                        newestVersion.flowBaseId,
                        newestVersion.version,
                    );
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flowVersions, docTypeId, fetchFlowDetail]);

    const handleBackToDocTypes = () => {
        navigate("/admin/doctypes");
    };

    const handleVersionChange = (version: FlowVersionSummaryViewModel) => {
        setSelectedVersion(version);
        if (docTypeId) {
            fetchFlowDetail(docTypeId, version.flowBaseId, version.version);
        }
    };

    const handleActivateFlow = async () => {
        if (!docTypeId || !selectedVersion) return;

        await activateFlow(docTypeId, {
            flow_base_id: selectedVersion.flowBaseId,
            version: selectedVersion.version,
        });
    };

    const handleOpenFormDialog = () => {
        setIsFormDialogOpen(true);
    };

    const handleCloseFormDialog = () => {
        setIsFormDialogOpen(false);
    };

    const handleFormSuccess = async () => {
        if (docTypeId) {
            setShouldAutoSelectNewest(true);
            await fetchFlowVersions(docTypeId);
        }
    };

    if (!docTypeId) {
        return (
            <div className="admin-page-content">
                <Alert color="red">Document type ID is required</Alert>
            </div>
        );
    }

    return (
        <div className="admin-page-content">
            <div className="doctype-flow-page__header">
                <div className="doctype-flow-page__header-left d-flex flex-row gap-2 justify-content-center">
                    <Button
                        variant="subtle"
                        leftSection={<IconArrowLeft size={16} />}
                        onClick={handleBackToDocTypes}
                        className="doctype-flow-page__back-button"
                    >
                        {t("admin.flowConfigs.actions.backToDocTypes")}
                    </Button>
                    <Text component="h1" size="xl" fw={700}>
                        {t("admin.flowConfigs.title")}
                    </Text>
                </div>
                <div className="doctype-flow-page__header-right">
                    {flowVersions.length > 0 ? (
                        <VersionSelector
                            versions={flowVersions}
                            selectedVersion={selectedVersion}
                            onVersionChange={handleVersionChange}
                            isLoading={isLoading}
                        />
                    ) : (
                        <Button
                            leftSection={<IconPlus size={16} />}
                            onClick={handleOpenFormDialog}
                        >
                            {t("admin.flowConfigs.actions.createFlowConfig")}
                        </Button>
                    )}
                </div>
            </div>

            {error && (
                <Alert color="red" className="doctype-flow-page__error">
                    {error}
                </Alert>
            )}

            {isLoading ? (
                <div className="doctype-flow-page__loading">
                    <Loader />
                </div>
            ) : !selectedFlowConfig ? (
                <Paper className="doctype-flow-page__empty-state">
                    <Text size="lg" c="dimmed" mb="xs">
                        {t("admin.flowConfigs.empty.noFlowConfigs")}
                    </Text>
                    <Text size="sm" c="dimmed">
                        {t("admin.flowConfigs.empty.description")}
                    </Text>
                </Paper>
            ) : (
                <FlowDiagram
                    flowConfig={selectedFlowConfig}
                    docTypeId={docTypeId}
                    docTypeCode={docTypeCode}
                    onActivate={handleActivateFlow}
                    onRefresh={() => fetchFlowVersions(docTypeId)}
                />
            )}

            <CreateFlowConfigDialog
                open={isFormDialogOpen}
                onClose={handleCloseFormDialog}
                onSuccess={handleFormSuccess}
                docTypeId={docTypeId}
                existingConfig={selectedFlowConfig}
            />
        </div>
    );
};
